const AppPath = require('@/lib/Navigation/AppPath');
const { toBool, toNullableFloat, toNullableInt, toNullableString } = require('@/functions/transform');

/**
 * SellState represents some options that can be applied to the Sell controller,
 * such as limiting what item types are shown, preventing additional items being added, paying instantly,
 * and where the user is taken when they have completed payment.
 *
 * The default values are the normal Sell behaviour - nothing hidden, no redirect when finished.
 *
 * @constructor
 */
const SellState = function () {
    /**
     * Only allow these item types to be added to the cart.
     * null = no restriction.
     * An empty array = no types are allowed.
     *
     * @see ItemType for possible types.
     *
     * @type {string[]|null}
     */
    this.restrictItemTypes = null;

    /**
     * An alternative to restrictItemTypes - this will cause the supplied item type tab to be pre-selected
     * when you go to the Sell page, but it will not prevent you switching to another item type tab.
     *
     * @type {string|null}
     */
    this.defaultItemTypeTab = null;

    /**
     * Automatically show the pay screen?
     * (Only works if items have been added to the cart already.)
     *
     * @type {boolean}
     */
    this.payNow = false;

    /**
     * If we are making a balance payment what is the value?
     * If this value is set we are making a balance payment and this is what we test against.
     * Do not set this directly. Use the setBalancePaymentAmount method.
     *
     * @type {number|null}
     */
    this.balancePaymentAmount = null;

    /**
     * Prevent any additional items being added to the cart?
     * If this is a string it will be displayed an error with the given string from the language file when the
     * user tries to add items to the cart.
     * You can set this to true to prevent adding items without specifying a message.
     *
     * @type {boolean|string}
     */
    this.preventAdditional = false;

    /**
     * Prevent items being removed from the cart?
     * To enable, set this to a string from the language file that will be shown to the user saying why
     * they cannot remove any items.
     * To prevent removal of individual cart items instead of everything in the cart,
     * call setCanRemove(false) on a CartItem.
     *
     * @type {boolean|string}
     */
    this.preventRemoval = false;

    /**
     * Prevent the qty of any items in the cart being changed?
     * To prevent adjusting the quantity of only individual cart items instead of everything in the cart,
     * call setCanAdjustQuantity(false) on a CartItem.
     *
     * @type {boolean}
     */
    this.preventQtyAdjustment = false;

    /**
     * Direct the user to this path when they either successfully complete the order.
     *
     * @type {AppPath|null}
     */
    this.successReturnPath = null;

    /**
     * Should a "Begin New Order" button be shown to the user in the change modal?
     * This button will reset the cart and allow them to create a new order.
     *
     * @type {boolean}
     */
    this.showNewOrderButtonAfterComplete = true;

    /**
     * Should a "View Order" button be shown to the user in the change modal?
     * This button will take the user to the newly created / edited order.
     *
     * @type {boolean}
     */
    this.showViewOrderButtonAfterComplete = true;

    /**
     * Query params appended when the "View Order" button is used.
     *
     * @type {{}}
     */
    this.viewOrderButtonPathQuery = {};

    /**
     * Direct the user to this path if they press the cancel button on the cart.
     *
     * @type {AppPath|null}
     */
    this.cancelCartReturnPath = null;

    /**
     * Direct the user to this path if they press the cancel button on the payment modal.
     * If this is null the user will be taken back to the cart as normal and will be able to
     * attempt payment again.
     *
     * @type {AppPath|null}
     */
    this.cancelPaymentReturnPath = null;

    /**
     * Direct the user to this path if they press the cancel button on the ticket picker modal.
     *
     * But only one time!
     * When the sell view loads it will pull this value out of the state so it only happens once.
     *
     * @type {AppPath|null}
     */
    this.cancelTicketPickerReturnPath = null;

    /**
     * How many items were added to the cart from the last user action?
     * If a toast is being shown after adding items to the cart, this is the number that will be shown in the toast.
     *
     * NOTE: This is currently only implemented when adding session tickets coming from the today screen
     * (with autoSubmitSessionID in the sell screen URL).
     *
     * @type {null}
     */
    this.lastAddedItemQty = null;

    /**
     * Has the user selected to print a tax (VAT) receipt?
     *
     * @type {boolean}
     */
    this.printTaxReceipt = false;

    /**
     * Should a toast be shown to the user when an item is added to the cart?
     *
     * NOTE: This is currently only implemented when adding session tickets coming from the today screen
     * (with autoSubmitSessionID in the sell screen URL).
     *
     * @type {boolean}
     */
    this.showItemAddedToast = false;

    /**
     * A path (including params) to redirect the user after adding an item to the cart.
     * For example to return the user back to the Today Screen after adding a session ticket to the cart.
     *
     * NOTE: This is currently only implemented when adding session tickets coming from the today screen
     * (with autoSubmitSessionID in the sell screen URL).
     *
     * @type {AppPath|null}
     */
    this.addItemReturnPath = null;

    /**
     * If set, when the sell view is shown it will automatically add this ticket to the cart.
     * Note that the ticket must be an event-based ticket not an open ticket.
     *
     * But only one time!
     * When the sell view loads it will pull this value out of the state so it only happens once.
     *
     * @type {number|null}
     */
    this.autoAddEventTicketID = null;

    /**
     * If set, when the sell view is shown it will automatically show the ticket picker with tickets for this
     * session('s event), then skip the session picker and automatically select this session.
     *
     * But only one time!
     * When the sell view loads it will pull this value out of the state so it only happens once.
     *
     * @type {number|null}
     */
    this.autoAddSessionID = null;

    /**
     * Stored product item ID object from product search tab.
     *
     * @type {number|null}
     */
    this.autoAddProductItemID = null;
};

SellState.prototype = {
    /**
     * Set related properties that go hand in hand with a balance payment.
     *
     * @param {number|null} amount
     */
    setBalancePaymentAmount(amount) {
        amount = parseFloat(amount);
        if (amount) {
            this.balancePaymentAmount = amount;
            this.payNow = true;
            this.preventAdditional = 'SELL.CANNOT_ADD_WHEN_PAYING_BALANCE';
            this.preventQtyAdjustment = true;
            this.preventRemoval = true;
        }
    },

    /**
     * @return {object}
     */
    getHydrationMap() {
        return {
            addItemReturnPath: {
                model: AppPath,
            },
            autoAddEventTicketID: toNullableInt,
            autoAddSessionID: toNullableInt,
            autoAddProductItemID: toNullableInt,
            balancePaymentAmount: toNullableFloat,
            cancelCartReturnPath: {
                model: AppPath,
            },
            cancelPaymentReturnPath: {
                model: AppPath,
            },
            cancelTicketPickerReturnPath: {
                model: AppPath,
            },
            defaultItemTypeTab: toNullableString,
            lastAddedItemQty: toNullableInt,
            payNow: toBool,
            preventAdditional: toBool,
            preventQtyAdjustment: toBool,
            preventRemoval: toBool,
            printTaxReceipt: toBool,
            restrictItemTypes: {},
            showItemAddedToast: toBool,
            successReturnPath: {
                model: AppPath,
            },
        };
    },

    /**
     * @private
     *
     * @param {string} key
     * @param {any} resetValue
     *
     * @returns {any}
     */
    pullAndReset(key, resetValue = null) {
        // Get the existing value.
        const value = this[key];

        // Reset it to the default value.
        this[key] = resetValue;

        // Return the value we pulled out.
        return value;
    },

    /**
     * @returns {number|null}
     */
    pullAutoAddSessionID() {
        return this.pullAndReset('autoAddSessionID');
    },

    /**
     * @returns {Number|null}
     */
    pullAutoAddProductItemID() {
        return this.pullAndReset('autoAddProductItemID');
    },

    /**
     * @returns {number|null}
     */
    pullAutoAddEventTicketID() {
        return this.pullAndReset('autoAddEventTicketID');
    },

    /**
     * @returns {AppPath|null}
     */
    pullCancelTicketPickerReturnPath() {
        return this.pullAndReset('cancelTicketPickerReturnPath');
    },

    /**
     * @returns {AppPath|null}
     */
    pullAddItemReturnPath() {
        return this.pullAndReset('addItemReturnPath');
    },

    /**
     * @returns {boolean}
     */
    pullShowItemAddedToast() {
        return this.pullAndReset('showItemAddedToast', false);
    },

    /**
     * @returns {number|null}
     */
    pullLastAddedItemQty() {
        return this.pullAndReset('lastAddedItemQty');
    },
};

module.exports = SellState;
